import React, { useState, useEffect } from 'react';
import {
  Play,
  Pause,
  SkipBack,
  SkipForward,
  Volume2,
  VolumeX,
  Loader,
  List,
  X,
  Maximize2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAudioPlayer } from '../context/AudioPlayerContext';
import FullScreenPlayer from './FullScreenPlayer';
import './GlobalAudioPlayer.css';

const GlobalAudioPlayer: React.FC = () => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [isDraggingProgress, setIsDraggingProgress] = useState(false);
  const [isDraggingVolume, setIsDraggingVolume] = useState(false);
  const [shouldMinimize, setShouldMinimize] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [volumeSupported, setVolumeSupported] = useState(true);

  const {
    state,
    togglePlay,
    nextTrack,
    previousTrack,
    setCurrentTime,
    setVolume,
    toggleQueue,
    playAudio
  } = useAudioPlayer();

  // Check if volume control is supported
  useEffect(() => {
    const audio = new Audio();
    try {
      audio.volume = 0.5;
      setVolumeSupported(true);
    } catch (e) {
      setVolumeSupported(false);
    }
  }, []);

  // Auto-minimize player after 10 seconds when no audio is playing
  useEffect(() => {
    let minimizeTimer: NodeJS.Timeout;
    let fadeTimer: NodeJS.Timeout;

    if (!state.currentAudio) {
      // Start fade-out after 8 seconds, minimize after 10 seconds
      fadeTimer = setTimeout(() => {
        setIsFadingOut(true);
      }, 8000);

      minimizeTimer = setTimeout(() => {
        setShouldMinimize(true);
      }, 10000);
    } else {
      // Clear timers and reset states when audio is present
      setShouldMinimize(false);
      setIsFadingOut(false);
    }

    return () => {
      if (minimizeTimer) clearTimeout(minimizeTimer);
      if (fadeTimer) clearTimeout(fadeTimer);
    };
  }, [state.currentAudio]);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (isDraggingProgress) {
        const progressBar = document.querySelector('.progress-bar') as HTMLElement;
        if (!progressBar) return;
        
        const rect = progressBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const percentage = Math.max(0, Math.min(1, clickX / width));
        const newTime = percentage * state.duration;
        setCurrentTime(newTime);
      }
      
      if (isDraggingVolume) {
        const volumeBar = document.querySelector('.volume-bar') as HTMLElement;
        if (!volumeBar) return;
        
        const rect = volumeBar.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const width = rect.width;
        const newVolume = Math.max(0, Math.min(1, clickX / width));
        setVolume(newVolume);
      }
    };

    const handleGlobalMouseUp = () => {
      setIsDraggingProgress(false);
      setIsDraggingVolume(false);
    };

    if (isDraggingProgress || isDraggingVolume) {
      document.addEventListener('mousemove', handleGlobalMouseMove);
      document.addEventListener('mouseup', handleGlobalMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [isDraggingProgress, isDraggingVolume, state.duration, setCurrentTime, setVolume]);

  // Show minimized player when shouldMinimize is true, isMinimized is true, or when there's no current audio
  if (shouldMinimize || isMinimized || !state.currentAudio) {
    return (
      <div className={`global-audio-player minimized ${shouldMinimize ? 'auto-minimized' : ''}`}>
        <button
          className="minimized-player-btn"
          onClick={() => {
            if (state.currentAudio) {
              // If there's a current audio, just expand the player without changing the track
              setShouldMinimize(false);
              setIsMinimized(false);
              setIsFadingOut(false);
            } else if (state.queue.length > 0) {
              // Only play the first item in queue if nothing is currently playing
              const firstInQueue = state.queue[0];
              playAudio(firstInQueue, state.queue, 'queue');
              setShouldMinimize(false);
              setIsMinimized(false);
              setIsFadingOut(false);
            } else {
              // Just show the queue panel
              toggleQueue();
              setShouldMinimize(false);
              setIsMinimized(false);
            }
          }}
          title={state.queue.length > 0 ? `Play Queue (${state.queue.length} items)` : 'Open Queue'}
        >
          {state.isPlaying ? <Pause size={32} /> : <Play size={32} />}
        </button>
      </div>
    );
  }

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
    const clickX = clientX - rect.left;
    const width = rect.width;
    const percentage = Math.max(0, Math.min(1, clickX / width));
    const newTime = percentage * state.duration;
    setCurrentTime(newTime);
  };

  const handleProgressMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDraggingProgress(true);
    handleProgressClick(e);
  };

  const handleProgressMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingProgress) return;
    handleProgressClick(e);
  };

  const handleProgressMouseUp = () => {
    setIsDraggingProgress(false);
  };

  // Touch event handlers for progress bar
  const handleProgressTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDraggingProgress(true);
    handleProgressClick(e);
  };

  const handleProgressTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingProgress) return;
    e.preventDefault(); // Prevent scrolling while dragging
    handleProgressClick(e);
  };

  const handleProgressTouchEnd = () => {
    setIsDraggingProgress(false);
  };

  const handleVolumeClick = (e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0]?.clientX || e.changedTouches[0]?.clientX : e.clientX;
    const clickX = clientX - rect.left;
    const width = rect.width;
    const newVolume = Math.max(0, Math.min(1, clickX / width));
    setVolume(newVolume);
  };

  const handleVolumeMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    setIsDraggingVolume(true);
    handleVolumeClick(e);
  };

  const handleVolumeMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingVolume) return;
    handleVolumeClick(e);
  };

  const handleVolumeMouseUp = () => {
    setIsDraggingVolume(false);
  };

  // Touch event handlers for volume bar
  const handleVolumeTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    setIsDraggingVolume(true);
    handleVolumeClick(e);
  };

  const handleVolumeTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingVolume) return;
    e.preventDefault(); // Prevent scrolling while dragging
    handleVolumeClick(e);
  };

  const handleVolumeTouchEnd = () => {
    setIsDraggingVolume(false);
  };

  const progressPercentage = state.duration ? (state.currentTime / state.duration) * 100 : 0;
  const volumePercentage = state.volume * 100;

  return (
    <>
      <div className={`global-audio-player ${isFadingOut ? 'fading-out' : ''}`}>
        <div className="player-window-controls">
          <button
            className="maximize-btn"
            onClick={() => setIsFullScreen(true)}
            title="Full Screen"
          >
            <Maximize2 size={18} />
          </button>
          <button
            className="minimize-btn"
            onClick={() => setIsMinimized(true)}
            title="Minimize Player"
          >
            <X size={18} />
          </button>
        </div>
        <div className="player-container">
        {/* Track Info */}
        <div className="track-info">
          <div className="track-image">
            {state.currentAudio.naatKhawan?.image ? (
              <img 
                src={state.currentAudio.naatKhawan.image} 
                alt={state.currentAudio.naatKhawan.name}
              />
            ) : (
              <div className="track-image-placeholder">
                <span>{state.currentAudio.naatKhawan?.name?.charAt(0) || 'N'}</span>
              </div>
            )}
          </div>
          <div className="track-details">
            <Link 
              to={`/naat/${state.currentAudio.id}`}
              className="track-title-link"
            >
              <h4 className="track-title">{state.currentAudio.title}</h4>
              {state.currentAudio.urduTitle && (
                <p className="track-title-urdu">{state.currentAudio.urduTitle}</p>
              )}
            </Link>
            {state.currentAudio.naatKhawan && (
              <Link 
                to={`/naatkhawan/${state.currentAudio.naatKhawanId}`}
                className="track-artist-link"
              >
                {state.currentAudio.naatKhawan.name}
              </Link>
            )}
          </div>
        </div>

        {/* Player Controls */}
        <div className="player-controls">
          {/* Progress Bar */}
          <div className="progress-section">
            <span className="time-display">{formatTime(state.currentTime)}</span>
            <div
              className={`progress-bar ${isDraggingProgress ? 'dragging' : ''}`}
              onClick={handleProgressClick}
              onMouseDown={handleProgressMouseDown}
              onMouseMove={handleProgressMouseMove}
              onMouseUp={handleProgressMouseUp}
              onTouchStart={handleProgressTouchStart}
              onTouchMove={handleProgressTouchMove}
              onTouchEnd={handleProgressTouchEnd}
            >
              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{ width: `${progressPercentage}%` }}
                />
                <div
                  className="progress-handle"
                  style={{ left: `${progressPercentage}%` }}
                />
              </div>
            </div>
            <span className="time-display">{formatTime(state.duration)}</span>
          </div>

          <div className="control-buttons">
            <button
              className="control-btn"
              onClick={previousTrack}
              disabled={state.currentIndex <= 0}
            >
              <SkipBack size={18} />
            </button>

            <button
              className="play-pause-btn"
              onClick={togglePlay}
              disabled={state.isLoading}
            >
              {state.isLoading ? (
                <Loader size={15} className="spinning" />
              ) : state.isPlaying ? (
                <Pause size={15} />
              ) : (
                <Play size={15} />
              )}
            </button>

            <button
              className="control-btn"
              onClick={nextTrack}
              disabled={state.currentIndex >= state.playlist.length - 1}
            >
              <SkipForward size={18} />
            </button>

            {/* Queue button moved here for mobile layout */}
            <button
              className="queue-btn mobile-queue-btn"
              onClick={toggleQueue}
              title={`Queue (${state.queue.length})`}
            >
              <List size={18} />
              {state.queue.length > 0 && (
                <span className="queue-count">{state.queue.length}</span>
              )}
            </button>
          </div>
        </div>

        {/* Volume Control - Queue button also here for desktop */}
        <div className="volume-control">
          <button
            className="queue-btn desktop-queue-btn"
            onClick={toggleQueue}
            title={`Queue (${state.queue.length})`}
          >
            <List size={18} />
            {state.queue.length > 0 && (
              <span className="queue-count">{state.queue.length}</span>
            )}
          </button>
          {volumeSupported ? (
            <>
              <button className="volume-btn" onClick={() => setVolume(state.volume === 0 ? 1 : 0)}>
                {state.volume === 0 ? <VolumeX size={18} /> : <Volume2 size={18} />}
              </button>
              <div
                className={`volume-bar ${isDraggingVolume ? 'dragging' : ''}`}
                onClick={handleVolumeClick}
                onMouseDown={handleVolumeMouseDown}
                onMouseMove={handleVolumeMouseMove}
                onMouseUp={handleVolumeMouseUp}
                onTouchStart={handleVolumeTouchStart}
                onTouchMove={handleVolumeTouchMove}
                onTouchEnd={handleVolumeTouchEnd}
              >
                <div className="volume-track">
                  <div
                    className="volume-fill"
                    style={{ width: `${volumePercentage}%` }}
                  />
                  <div
                    className="volume-handle"
                    style={{ left: `${volumePercentage}%` }}
                  />
                </div>
              </div>
            </>
          ) : (
            <span className="volume-not-supported" title="Volume control not supported on this device">
              <Volume2 size={18} style={{ opacity: 0.3 }} />
            </span>
          )}
        </div>
      </div>
    </div>
    <FullScreenPlayer
      isOpen={isFullScreen}
      onClose={() => setIsFullScreen(false)}
    />
    </>
  );
};

export default GlobalAudioPlayer;